/**
 * Guard against the portal booting inside one of its own legacy iframes.
 *
 * CustomIFrame embeds PHP pages. When the PHP side can't log the user in (or
 * any of its links point back at the portal host), the iframe navigates to the
 * portal and a second copy of the app boots inside the first — nested
 * dashboard, nested 404, and every click inside it opens yet another level.
 *
 * The nested copy detects this before rendering, tells the top portal, and
 * stays blank. The top portal's CustomIFrame then retries or shows an error.
 */

export const NESTED_PORTAL_MESSAGE = 'ilm:nested-portal'
const PING_MESSAGE = 'ilm:portal-ping'
const PONG_MESSAGE = 'ilm:portal-pong'
const PING_TIMEOUT_MS = 400

export type NestedPortalMessage = {type: typeof NESTED_PORTAL_MESSAGE; path: string}

const isFramed = (win: Window) => {
  try {
    return win.self !== win.top
  } catch {
    return true
  }
}

/** Origin of the top portal when it is same-origin with us, otherwise null. */
const sameOriginTop = (win: Window): string | null => {
  try {
    const origin = win.top?.location.origin
    return origin === win.location.origin ? origin : null
  } catch {
    // cross-origin top: reading location throws
    return null
  }
}

/**
 * Ask the top window whether it is a portal. Only used when the top is
 * cross-origin (e.g. PHP redirected to a different portal host than the one
 * the user opened), so normal top-level loads never wait on it.
 */
const pingTop = (win: Window): Promise<string | null> =>
  new Promise((resolve) => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== win.top || event.data?.type !== PONG_MESSAGE) return
      done(event.origin)
    }
    const done = (origin: string | null) => {
      win.removeEventListener('message', onMessage)
      clearTimeout(timer)
      resolve(origin)
    }
    const timer = setTimeout(() => done(null), PING_TIMEOUT_MS)
    win.addEventListener('message', onMessage)
    win.top?.postMessage({type: PING_MESSAGE}, '*')
  })

/**
 * Resolves true when this window is a portal running inside another portal.
 * In that case the top portal has already been notified.
 */
export const detectNestedPortal = async (win: Window = window): Promise<boolean> => {
  if (!isFramed(win)) return false

  const topOrigin = sameOriginTop(win) ?? (await pingTop(win))
  if (!topOrigin) return false

  const message: NestedPortalMessage = {
    type: NESTED_PORTAL_MESSAGE,
    path: win.location.pathname + win.location.search,
  }
  win.top?.postMessage(message, topOrigin)
  return true
}

/** Lets nested portals on another origin find us (see pingTop). Top-level portal only. */
export const answerPortalPings = (win: Window = window) => {
  win.addEventListener('message', (event) => {
    if (event.data?.type !== PING_MESSAGE || !event.source) return
    ;(event.source as Window).postMessage({type: PONG_MESSAGE}, event.origin)
  })
}

/** True when `source` is `frame` or any window nested below it. */
export const isWindowInside = (source: MessageEventSource | null, frame: Window | null) => {
  if (!source || !frame) return false
  let current = source as Window
  // window.parent is readable across origins, so this walk works for any mix
  for (let depth = 0; depth < 10; depth++) {
    if (current === frame) return true
    if (current.parent === current) return false
    current = current.parent
  }
  return false
}
